import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Stack, InputAdornment, Grid,
    Typography, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Hash, Building, Tag, Briefcase } from 'lucide-react';

export const BillingForm = ({
  paymentMethod: initialPaymentMethod,
  setPaymentMethod,
  yNumber: initialYNumber,
  setYNumber,
  companyName: initialCompanyName,
  setCompanyName,
  paymentReference: initialPaymentReference,
  setPaymentReference,
  billingDepartment: initialBillingDepartment,
  setBillingDepartment,
}) => {
  const { t } = useTranslation();

  const [paymentMethod,     setLocalPaymentMethod]     = useState(initialPaymentMethod     ?? 'invoice');
  const [yNumber,           setLocalYNumber]           = useState(initialYNumber           ?? '');
  const [companyName,       setLocalCompanyName]       = useState(initialCompanyName       ?? '');
  const [paymentReference,  setLocalPaymentReference]  = useState(initialPaymentReference  ?? '');
  const [billingDepartment, setLocalBillingDepartment] = useState(initialBillingDepartment ?? '');

  useEffect(() => { setLocalPaymentMethod(initialPaymentMethod         ?? 'invoice'); }, [initialPaymentMethod]);
  useEffect(() => { setLocalYNumber(initialYNumber                     ?? ''); },       [initialYNumber]);
  useEffect(() => { setLocalCompanyName(initialCompanyName             ?? ''); },       [initialCompanyName]);
  useEffect(() => { setLocalPaymentReference(initialPaymentReference   ?? ''); },       [initialPaymentReference]);
  useEffect(() => { setLocalBillingDepartment(initialBillingDepartment ?? ''); },       [initialBillingDepartment]);

  const handlePaymentMethodChange   = useCallback((method) => { setLocalPaymentMethod(method); setPaymentMethod?.(method); }, [setPaymentMethod]);
  const handleYNumberBlur           = useCallback(() => setYNumber?.(yNumber),                    [setYNumber,          yNumber]);
  const handleCompanyNameBlur       = useCallback(() => setCompanyName?.(companyName),             [setCompanyName,      companyName]);
  const handlePaymentReferenceBlur  = useCallback(() => setPaymentReference?.(paymentReference),   [setPaymentReference, paymentReference]);
  const handleBillingDepartmentBlur = useCallback(() => setBillingDepartment?.(billingDepartment), [setBillingDepartment,billingDepartment]);

  return (
    <Stack spacing={2}>
      {/* Payment method toggle */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('delivery.paymentMethod') || 'Payment method'}
        </Typography>
        {['invoice', 'card'].map((method, idx) => (
          <React.Fragment key={method}>
            {idx > 0 && <Typography variant="caption" color="text.disabled">·</Typography>}
            <Typography
              variant="caption"
              onClick={() => handlePaymentMethodChange(method)}
              sx={{
                cursor: 'pointer',
                fontWeight: paymentMethod === method ? 700 : 400,
                color: paymentMethod === method ? 'primary.main' : 'text.secondary',
                textDecoration: paymentMethod === method ? 'underline' : 'none',
              }}
            >
              {method === 'invoice' ? (t('delivery.invoice') || 'Invoice') : (t('delivery.card') || 'Card')}
            </Typography>
          </React.Fragment>
        ))}
      </Stack>

      {paymentMethod === 'invoice' ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth size="small"
                label={t('delivery.companyName') || 'Company Name'}
                placeholder="Your Company Ltd"
                value={companyName}
                onChange={(e) => setLocalCompanyName(e.target.value)}
                onBlur={handleCompanyNameBlur}
                InputProps={{ startAdornment: <InputAdornment position="start"><Building size={16} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small"
                label={t('delivery.yNumber') || 'Business ID'}
                placeholder="1234567-8"
                value={yNumber}
                onChange={(e) => setLocalYNumber(e.target.value)}
                onBlur={handleYNumberBlur}
                InputProps={{ startAdornment: <InputAdornment position="start"><Hash size={16} /></InputAdornment> }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={t('delivery.paymentReference') || 'Reference'}
                placeholder="Project code / reference"
                value={paymentReference}
                onChange={(e) => setLocalPaymentReference(e.target.value)}
                onBlur={handlePaymentReferenceBlur}
                InputProps={{ startAdornment: <InputAdornment position="start"><Tag size={16} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={t('delivery.billingDepartment') || 'Department'}
                placeholder="Cost center / department"
                value={billingDepartment}
                onChange={(e) => setLocalBillingDepartment(e.target.value)}
                onBlur={handleBillingDepartmentBlur}
                InputProps={{ startAdornment: <InputAdornment position="start"><Briefcase size={16} /></InputAdornment> }}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
          <Typography variant="body2" color="info.dark">
            {t('delivery.cardPaymentNote') || 'Payment will be processed after delivery is submitted.'}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};